import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { CircularProgress, Tooltip } from "@material-ui/core";
import { useParams } from "react-router-dom";
import simpleHtmlExport from "../../drawer/new-tldraw-components/simpleHtmlExport";
import { generateFileFromService } from "../../../../../module/puppeteerService/generatePDF";
import { getFormatDimensionsWithOrientation } from "../../utils/formatDimensions";
import { useVariables } from "../../../../../components/VariablesPanel/context/VariablesContext";
import { getStoredOrientation } from "../../utils/orientationStorage";
import { usePDFUpload } from "../../drawer/contexts/PDFUploadContext";
import useTooltipStyles from "../../drawer/components/style";

const checkboxOptions = [
  {
    id: "crop-marks",
    label: "Crop marks and bleed",
    // Short copy aligned with the redesigned compact modal — long
    // print-jargon explanation was replaced by a single user-facing
    // sentence.
    tooltip: "Prevents text and logos from being cut off during trimming.",
  },
  {
    id: "display-safety-and-cut-lines",
    label: "Display safety and cut lines",
    tooltip: "Displays margin and trim guides in the export.",
  },
  {
    id: "flatten-pdf",
    label: "Flatten PDF",
    tooltip: "Merges all layers into one image and disables editing.",
  },
];

// 16×16 info icon copied from VariablesPanelV2 so the export modal's
// tooltips visually match the rest of the builder's "what is this?"
// affordances.
const InfoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ cursor: "pointer", flexShrink: 0 }}
  >
    <path
      d="M14.1666 7.9987C14.1666 4.59294 11.4057 1.83203 7.99998 1.83203C4.59422 1.83203 1.83331 4.59294 1.83331 7.9987C1.83331 11.4045 4.59422 14.1654 7.99998 14.1654C11.4057 14.1654 14.1666 11.4045 14.1666 7.9987ZM7.49998 10.6654V7.9987C7.49998 7.72256 7.72384 7.4987 7.99998 7.4987C8.27612 7.4987 8.49998 7.72256 8.49998 7.9987V10.6654C8.49998 10.9415 8.27612 11.1654 7.99998 11.1654C7.72384 11.1654 7.49998 10.9415 7.49998 10.6654ZM8.00649 4.83203C8.28263 4.83203 8.50649 5.05589 8.50649 5.33203C8.50649 5.60817 8.28263 5.83203 8.00649 5.83203H7.99998C7.72384 5.83203 7.49998 5.60817 7.49998 5.33203C7.49998 5.05589 7.72384 4.83203 7.99998 4.83203H8.00649ZM15.1666 7.9987C15.1666 11.9567 11.958 15.1654 7.99998 15.1654C4.04194 15.1654 0.833313 11.9567 0.833313 7.9987C0.833313 4.04066 4.04194 0.832031 7.99998 0.832031C11.958 0.832031 15.1666 4.04066 15.1666 7.9987Z"
      fill="#222336"
    />
  </svg>
);

// 16×16 close (X) icon used in the new modal header. Matches the
// 1.5px stroke / #292D32 colour spec from the design.
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4 4L12 12M12 4L4 12" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Chevron-down for the file-type dropdown — replaces the bigger,
// rounder one used before so it fits the slimmer 38px field height.
const ChevronDownIcon = ({ rotated }: { rotated: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{
      transition: "transform 0.15s ease",
      transform: rotated ? "rotate(180deg)" : "none",
    }}
  >
    <path d="M4 6L8 10L12 6" stroke="#2E3860" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FILE_LABEL_TO_VALUE = {
  JPG: "JPEG",
  PNG: "PNG",
  "PDF (CMYK)": "PDF_CMYK",
  "PDF (RGB)": "PDF_RGB",
};

const FILE_FORMAT_TOOLTIPS = {
  JPG: "JPEG format - Compressed image file ideal for web use and email. Smaller file size but may have some quality loss.",
  PNG: "PNG format - High-quality image file with transparency support. Larger file size but perfect quality.",
  "PDF (CMYK)": "PDF with CMYK colors - Best for professional printing. Colors are optimized for print production.",
  "PDF (RGB)": "PDF with RGB colors - Best for digital use and online sharing. Colors are optimized for screens.",
};

interface ExportButtonProps {
  format: string;
  editor?: any; // Optional editor instance
}

const ExportButton = ({ format, editor }: ExportButtonProps) => {
  const searchParams = new URLSearchParams(window.location.search);
  const isIntegrated = searchParams.get("variant") === "integrated";
  const { isUploading: isPdfUploading } = usePDFUpload();
  const { campaignId, clientId, roomId: roomIdParam } = useParams();
  // Match Settings.tsx logic: read designId from query string first, then route params
  const designId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("designId") || roomIdParam;
  }, [roomIdParam]);

  // Shared tooltip styling — same look used across the flyer builder
  // (alignment row, tool island, corner-radius popover, etc.) so the
  // export modal's tooltips don't visually break from the rest.
  const tooltipClasses = useTooltipStyles();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  // State to manage dropdown visibility
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // State to store the selected file type
  const [selectedOption, setSelectedOption] = useState("PDF (CMYK)");
  const [checkboxOptionsState, setCheckboxOptionsState] = useState(() => ({
    "crop-marks": false,
    "display-safety-and-cut-lines": false,
    "flatten-pdf": false,
    "include-notes": false,
  }));
  // Ref to detect clicks outside the dropdown
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Ref to detect clicks outside the modal
  const modalRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Use variables context to get active variables and selected lists
  const {
    activeVariables,
    selectedMailingList,
    selectedCouponList,
    uploadedMailingLists,
    uploadedCouponLists,
    previewMode,
    setPreviewMode,
  } = useVariables();

  const fileOptions = Object.keys(FILE_LABEL_TO_VALUE);

  // Toggles the dropdown open/closed
  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  // Handles selecting an option from the dropdown
  const handleOptionClick = (option: string) => {
    const nextIsPdf = option === "PDF (CMYK)" || option === "PDF (RGB)";
    setIsDropdownOpen(false);
    setSelectedOption(option);
    if (!nextIsPdf && checkboxOptionsState["flatten-pdf"]) {
      setCheckboxOptionsState((prev) => ({
        ...prev,
        "flatten-pdf": false,
      }));
    }
  };

  const handleCheckboxClick = (option: keyof typeof checkboxOptionsState) => {
    setCheckboxOptionsState({
      ...checkboxOptionsState,
      [option]: !checkboxOptionsState[option],
    });
  };

  const handleExport = async (fileType: string, checkboxOptionsState: any) => {
    if (isPdfUploading) return;
    setIsModalOpen(false);
    setLoading(true);

    // Clear preview mode before export to ensure {{variables}} are exported, not preview values
    if (previewMode !== "none") {
      setPreviewMode("none");
    }

    try {
      const fileTypeValue = FILE_LABEL_TO_VALUE[fileType as keyof typeof FILE_LABEL_TO_VALUE] as
        | "JPEG"
        | "PNG"
        | "PDF_CMYK"
        | "PDF_RGB";

      const htmlContent = await simpleHtmlExport({
        designId,
        campaignId,
        format,
        returnHtml: true,
        editor, // Pass editor instance for camera control
        keepCameraPosition: true, // Keep camera centered after export
        displaySafetyAndCutLines: checkboxOptionsState["display-safety-and-cut-lines"],
      });

      if (!htmlContent) {
        throw new Error("Failed to generate HTML content");
      }

      // // 🐛 DEBUG: Save HTML to @tmp/ folder for inspection (local dev only)
      if (process.env.REACT_APP_DESIGN_HTML_DEBUG === "true") {
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const debugFileName = `debug-export-${format}-${fileTypeValue}-${timestamp}.html`;

        // Save debug HTML using the new simple export (will be saved to @tmp/)
        await simpleHtmlExport({
          designId,
          campaignId,
          format,
          returnHtml: false, // Trigger download
          fileName: debugFileName,
          editor, // Pass editor instance for camera control
        });
      }

      // console.log(`🐛 DEBUG: Saved HTML file to @tmp/ for inspection: @tmp/${debugFileName}`);
      console.log(`📄 HTML content length: ${htmlContent.length} characters`);

      // Calculate dimensions in millimeters from format with orientation support for precise PDF generation
      const customDimensions = getFormatDimensionsWithOrientation(format, designId, campaignId);

      console.log(`📐 Sending dimensions for format ${format}:`, customDimensions, "mm");

      const isImage = fileTypeValue === "JPEG" || fileTypeValue === "PNG";

      // Calculate image resolution for images: width = (page width x 2) + 10px, height = page height
      // Use actual pixel dimensions from FORMATS and account for dynamic orientation
      let imageResolution;
      if (isImage) {
        // Import FORMATS to get actual pixel dimensions and orientation enum
        const { getEffectiveFormat, isRuntimeFormat } = await import("../../formats/formatsStore");
        const { DocumentOrientation } = await import("../../drawer/types");
        const formatConfig = getEffectiveFormat(format);

        if (formatConfig) {
          // Get stored orientation from localStorage using the new pattern with design/campaign ID
          const storedOrientation = getStoredOrientation(
            designId,
            campaignId,
            formatConfig.defaultOrientation ?? DocumentOrientation.Portrait
          );
          const isPortrait = storedOrientation === DocumentOrientation.Portrait;

          // Some formats don't support landscape (TEARAWAY, DOOR_HANGER)
          const isLandscapeEnabled = isRuntimeFormat(formatConfig)
            ? formatConfig.allowLandscape
            : format !== "TEARAWAY" && format !== "DOOR_HANGER";

          // Detect natural orientation of the format config
          const cfgW = formatConfig.displayWidth || formatConfig.width || 419.5;
          const cfgH = formatConfig.displayHeight || formatConfig.height || 582;
          const isNaturallyLandscape = cfgW > cfgH;

          let pageWidthPx, pageHeightPx;

          if (isPortrait) {
            if (isNaturallyLandscape) {
              // Config is landscape but user wants portrait — swap
              pageWidthPx = cfgH;
              pageHeightPx = cfgW;
            } else {
              pageWidthPx = cfgW;
              pageHeightPx = cfgH;
            }
          } else if (!isLandscapeEnabled) {
            pageWidthPx = cfgW;
            pageHeightPx = cfgH;
          } else {
            // Landscape
            if (!isNaturallyLandscape) {
              // Config is portrait but user wants landscape — swap
              pageWidthPx = cfgH;
              pageHeightPx = cfgW;
            } else {
              pageWidthPx = cfgW;
              pageHeightPx = cfgH;
            }
          }

          imageResolution = {
            width: Math.round(pageWidthPx * 2 + 10),
            height: Math.round(pageHeightPx),
          };

          console.log(
            `📐 Image resolution for format ${format} (${
              isPortrait ? "Portrait" : "Landscape"
            }): ${pageWidthPx}px × ${pageHeightPx}px → ${imageResolution.width} × ${imageResolution.height}`
          );
        }
      }

      const res = await generateFileFromService({
        htmlString: htmlContent,
        fileType: fileTypeValue,
        includeBleedsAndCropMarks: checkboxOptionsState["crop-marks"],
        flattenPDF:
          (fileTypeValue === "PDF_CMYK" || fileTypeValue === "PDF_RGB") && checkboxOptionsState["flatten-pdf"],
        customDimensions, // Send precise dimensions in millimeters
        useRowLayout: isImage,
        ...(imageResolution && { imageResolution }), // Add imageResolution for images
      });

      console.log("res", res);
      console.log("res.s3Url", res.s3Url);

      if (!res.s3Url) {
        throw new Error("No download URL received from server");
      }

      // Primary method: trigger download directly without opening new tab
      console.log("Triggering direct download for:", res.s3Url);
      try {
        const link = document.createElement("a");
        link.href = res.s3Url;
        link.download = `design-export.${fileTypeValue.toLowerCase()}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log("Direct download triggered successfully");
      } catch (error) {
        console.warn("Direct download failed, trying fallback methods:", error);

        // Fallback 1: Try to open download in new tab
        let resWindow: Window | null = null;

        try {
          // First try opening in current window context
          resWindow = window.open(res.s3Url, "_blank");
        } catch (error) {
          console.warn("Failed to open in current window context:", error);
        }

        // Fallback 2: If that failed or was blocked, try opening in parent window (for iframe cases)
        if (!resWindow || resWindow.closed) {
          try {
            resWindow = window.parent.open(res.s3Url, "_blank");
            console.log("Opened in parent window");
          } catch (error) {
            console.warn("Failed to open in parent window context:", error);
          }
        }

        console.log("resWindow", resWindow);
        if (resWindow && !resWindow.closed) {
          resWindow.focus();
        } else {
          console.error("All download methods failed");
        }
      }
    } catch (error) {
      console.error("Export failed:", error);
      // Show error message to user
      // alert(`Export failed: ${error instanceof Error ? error.message : "Unknown error occurred"}`);
    } finally {
      setLoading(false);
    }
  };

  // Effect to handle clicks outside of the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsModalOpen(false);
      }
    };
    // Add event listener when the component mounts
    document.addEventListener("mousedown", handleClickOutside);
    // Clean up event listener when the component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Track button position to anchor the modal below the button even in iframe/resize/scroll/format change
  useEffect(() => {
    const updateAnchor = () => {
      if (buttonRef.current) {
        setAnchorRect(buttonRef.current.getBoundingClientRect());
      }
    };
    if (isModalOpen) {
      updateAnchor();
      window.addEventListener("resize", updateAnchor);
      window.addEventListener("scroll", updateAnchor, true);
    }
    return () => {
      window.removeEventListener("resize", updateAnchor);
      window.removeEventListener("scroll", updateAnchor, true);
    };
  }, [isModalOpen, format]);
  // New compact spec: 271px wide modal anchored under the trigger.
  const modalWidth = 271;

  const handleOpen = () => {
    if (isPdfUploading) return;
    if (buttonRef.current) {
      setAnchorRect(buttonRef.current.getBoundingClientRect());
    }
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIsDropdownOpen(false);
  };

  const isPdf = selectedOption === "PDF (CMYK)" || selectedOption === "PDF (RGB)";
  const visibleCheckboxOptions = checkboxOptions.filter((option) => option.id !== "flatten-pdf" || isPdf);

  const renderModal = () => {
    if (!isModalOpen || !anchorRect) return null;

    const left = Math.max(
      8,
      Math.min(
        (anchorRect.left || 0) + (anchorRect.width || 0) / 2 - modalWidth / 2 - 80,
        (window.innerWidth || 0) - modalWidth - 8
      )
    );
    const top = (anchorRect.bottom || 0) + 8;

    const modalContent = (
      <div
        className="fixed inset-0 z-[1100]"
        onMouseDown={(e) => {
          if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
            handleClose();
          }
        }}
      >
        {/* Outer container — the spec's 271px wide flex column. The
            header and body are rendered as separate self-stretched
            sections so each can own its own border + rounded corners
            (top vs. bottom), matching the seamless one-card look. */}
        <div
          ref={modalRef}
          data-cypress-id="export-modal"
          style={{
            position: "absolute",
            top,
            left,
            display: "flex",
            width: 271,
            flexDirection: "column",
            alignItems: "flex-start",
            fontFamily: '"Inter", sans-serif',
            boxShadow: "0 2px 12px 0 rgba(37, 59, 97, 0.10)",
            borderRadius: 8,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* HEADER — "Download" title + close X */}
          <div
            style={{
              display: "flex",
              padding: "16px 16px 8px 16px",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: 8,
              alignSelf: "stretch",
              borderRadius: "8px 8px 0 0",
              borderTop: "1px solid #E2E2FC",
              borderRight: "1px solid #E2E2FC",
              borderLeft: "1px solid #E2E2FC",
              background: "#FFF",
            }}
          >
            <div
              style={{
                display: "flex",
                height: 24,
                justifyContent: "space-between",
                alignItems: "center",
                alignSelf: "stretch",
              }}
            >
              <span
                style={{
                  color: "#2E3860",
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  fontFamily: '"Inter", sans-serif',
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: "16px",
                }}
              >
                Download
              </span>
              <button
                type="button"
                // Guard against closing while an export is in flight
                // — the request can't be cancelled and a half-closed
                // UI looks broken. `loading` is the only blocker; the
                // X is fully clickable in every other state.
                // `stopPropagation` keeps the click from bubbling up
                // to the outer click-outside-to-close handler so we
                // own the close path entirely.
                onClick={(e) => {
                  e.stopPropagation();
                  if (loading) return;
                  handleClose();
                }}
                disabled={loading}
                data-cypress-id="export-modal-close-button"
                aria-label="Close download modal"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                }}
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* BODY — File type subtitle + dropdown + options + Export */}
          <div
            style={{
              display: "flex",
              padding: "8px 16px 16px 16px",
              flexDirection: "column",
              alignItems: "flex-start",
              alignSelf: "stretch",
              gap: 8,
              borderRadius: "0 0 8px 8px",
              borderRight: "1px solid #E2E2FC",
              borderBottom: "1px solid #E2E2FC",
              borderLeft: "1px solid #E2E2FC",
              background: "#FFF",
            }}
          >
            {/* "File type" subtitle */}
            <span
              style={{
                color: "#2E3860",
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontFamily: '"Inter", sans-serif',
                fontSize: 14,
                fontWeight: 400,
                lineHeight: "16px",
              }}
            >
              File type
            </span>

            {/* File type dropdown — keeps existing functionality
                (toggleDropdown / handleOptionClick / fileOptions /
                FILE_FORMAT_TOOLTIPS), just restyled to the 38px slim
                pill from the spec. Fixed width 240px per design. */}
            <div style={{ position: "relative", width: 240 }} ref={dropdownRef}>
              <button
                type="button"
                onClick={toggleDropdown}
                data-cypress-id="export-modal-file-type-dropdown"
                style={{
                  display: "flex",
                  width: 214,
                  height: 38,
                  padding: "0 12px",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderRadius: 8,
                  border: "1px solid #E2E2FC",
                  background: "#FFF",
                  cursor: "pointer",
                  fontFamily: '"Inter", sans-serif',
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: "20px",
                  color: "#2E3860",
                }}
              >
                <span>{selectedOption}</span>
                <ChevronDownIcon rotated={isDropdownOpen} />
              </button>

              {isDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    background: "#FFF",
                    border: "1px solid #E2E2FC",
                    borderRadius: 8,
                    boxShadow: "0 2px 8px 0 rgba(37, 59, 97, 0.08)",
                    zIndex: 20,
                    overflow: "hidden",
                  }}
                >
                  {fileOptions.map((option) => (
                    <Tooltip
                      key={option}
                      title={FILE_FORMAT_TOOLTIPS[option as keyof typeof FILE_FORMAT_TOOLTIPS] || ""}
                      placement="left"
                      arrow
                      classes={{ tooltip: tooltipClasses.tooltip, arrow: tooltipClasses.arrow }}
                    >
                      <button
                        type="button"
                        onClick={() => handleOptionClick(option)}
                        data-cypress-id={`export-modal-file-type-option-${option}`}
                        style={{
                          display: "block",
                          width: "100%",
                          padding: "8px 12px",
                          textAlign: "left",
                          background: option === selectedOption ? "#F5F5FD" : "#FFF",
                          border: "none",
                          cursor: "pointer",
                          fontFamily: '"Inter", sans-serif',
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: "20px",
                          color: "#2E3860",
                        }}
                      >
                        {option}
                      </button>
                    </Tooltip>
                  ))}
                </div>
              )}
            </div>

            {/* Option rows — same toggle functionality as the previous
                checkboxes (flatten-pdf is still hidden for non-PDF
                file types via `visibleCheckboxOptions`). Each row is
                clickable to toggle; the info icon on the right end
                shows the short user-facing tooltip from the spec.
                A pink checkbox on the left preserves the existing
                selection affordance — the spec describes the label +
                info icon but the underlying toggle behaviour stays
                unchanged. */}
            <div style={{ display: "flex", flexDirection: "column", alignSelf: "stretch", gap: 8, paddingTop: 4 }}>
              {visibleCheckboxOptions.map((option) => {
                const isChecked = checkboxOptionsState[option.id as keyof typeof checkboxOptionsState];
                return (
                  <div
                    key={option.id}
                    onClick={() => handleCheckboxClick(option.id as keyof typeof checkboxOptionsState)}
                    data-cypress-id={`export-modal-option-${option.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      alignSelf: "stretch",
                      gap: 8,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        readOnly
                        style={{
                          width: 16,
                          height: 16,
                          margin: 0,
                          accentColor: "#EE4360",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          color: "#2E3860",
                          fontFeatureSettings: "'liga' off, 'clig' off",
                          fontFamily: '"Inter", sans-serif',
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: "20px",
                        }}
                      >
                        {option.label}
                      </span>
                    </div>
                    <Tooltip
                      title={option.tooltip}
                      placement="top"
                      arrow
                      classes={{ tooltip: tooltipClasses.tooltip, arrow: tooltipClasses.arrow }}
                    >
                      <span
                        // Stop the row click so opening the tooltip
                        // doesn't toggle the option.
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: "inline-flex" }}
                      >
                        <InfoIcon />
                      </span>
                    </Tooltip>
                  </div>
                );
              })}
            </div>

            {/* Export button */}
            <button
              type="button"
              onClick={() => handleExport(selectedOption, checkboxOptionsState)}
              data-cypress-id="export-modal-export-button"
              style={{
                display: "flex",
                padding: "8px 12px",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
                alignSelf: "stretch",
                marginTop: 8,
                borderRadius: 8,
                border: "none",
                background: "#EE4360",
                color: "#FFF",
                textAlign: "center",
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontFamily: '"Inter", sans-serif',
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "16px",
                cursor: "pointer",
              }}
            >
              Export
            </button>
          </div>
        </div>
      </div>
    );

    return ReactDOM.createPortal(modalContent, document.body);
  };

  const renderBlockingOverlay = () => {
    if (!loading) return null;
    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[1200] bg-transparent pointer-events-auto cursor-wait" />,
      document.body
    );
  };

  return (
    <Tooltip title={isPdfUploading ? "Please wait, PDF is importing..." : ""} placement="bottom" arrow>
      <div
        ref={buttonRef}
        onClick={handleOpen}
        className={classNames(
          isIntegrated
            ? "flex flex-col justify-center items-center rounded-[12px] bg-[#EE4360] text-white font-bold text-base leading-5 transition-all duration-300 mx-1.5 relative w-[124px] h-[40px] px-3 py-[10px] gap-[10px]"
            : "bg-white border-2 border-solid border-[#1a2547] rounded-2xl h-12 text-[#1a2547] font-bold text-base leading-5 transition-all duration-300 mx-1.5 flex items-center justify-center relative shadow-lg w-[148px]",
          isPdfUploading ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:scale-[1.02]"
        )}
      >
        {loading ? (
          // White spinner explicitly — MUI's CircularProgress defaults
          // to the theme's primary blue, which clashes against both
          // variants (pink integrated bg, dark text on white otherwise).
          // White matches the integrated pink button's text colour and
          // reads cleanly on the non-integrated variant too.
          <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
        ) : (
          <div className={isIntegrated ? "text-white" : "text-[#1a2547]"}>Export</div>
        )}
        {renderModal()}
        {renderBlockingOverlay()}
      </div>
    </Tooltip>
  );
};

export default ExportButton;
